/**
 * Početna (Luka's APP1): "Odvoji trenutak za sebe", the "Počni ovdje" hero that plays the free
 * first session, "Nastavi slušanje" while a session is unfinished on this device (Q23), the
 * Akustične / Vođene cards, the book card (only when the server shows the book), the need chips
 * and the "Kratka pauza za sebe" carousel.
 * What it shows is curated in `@/config/home` (Q22). Public: works signed out.
 */
import { useId, useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Loader2, Play } from 'lucide-react';
import { HorizontalCarousel, SessionTile } from '@/components/features';
import { BookHomeCard } from '@/components/features/book/BookCard';
import { SESSION_IMAGE_GRADIENT, SessionImageFallback } from '@/components/ui';
import { useFavorites, useSessions } from '@/hooks';
import {
  useAudioPlayerStore,
  useIsAuthenticated,
  useLanguage,
  useListeningResume,
  usePlaySession,
  useResumeSession,
  useUser,
} from '@/store';
import { ROUTES } from '@/config/routes';
import { isAcousticEnabled } from '@/config/features';
import { HOME_CURATION, HOME_IMAGES, type HomeImageSlot } from '@/config/home';
import { hasAccess } from '@/utils/access';
import { cn } from '@/utils/cn';
import { isUnfinished, pickFirstSession, pickShortBreak } from '@/utils/homeCuration';
import { formatPlaybackTime } from '@/utils/sessionBrowse';
import {
  getBottomNavTranslation,
  getHomeTranslation,
  getSessionsTranslation,
  type HomeTranslationKey,
} from '@/utils/translations';
import type { LocalizedSession, Session } from '@/types';

type SessionData = Session | LocalizedSession;

const FOCUS_RING = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

/** A configured photo, or its S1-palette placeholder until Luka's photos arrive (R13). Decorative. */
function HomeImageFill({ slot }: { slot: HomeImageSlot }) {
  const image = HOME_IMAGES[slot];
  if (image.src) {
    return <img src={image.src} alt="" data-home-image={slot} className="absolute inset-0 h-full w-full object-cover" />;
  }
  return <div aria-hidden="true" data-home-image={slot} className="absolute inset-0" style={{ backgroundImage: image.placeholder }} />;
}

interface StartButtonProps {
  label: string;
  isLoading: boolean;
  firstSession: SessionData | null;
  onPlay: (session: SessionData) => void;
}

function StartButton({ label, isLoading, firstSession, onPlay }: StartButtonProps) {
  const className = cn(
    'inline-flex min-h-11 items-center gap-1 self-start rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 disabled:opacity-60',
    FOCUS_RING,
  );
  const content = (
    <>
      {label}
      <ChevronRight className="h-4 w-4" aria-hidden="true" />
    </>
  );
  // No free session in the list (or it failed to load): Vođene is the next best start.
  if (!isLoading && !firstSession) {
    return (
      <Link to={ROUTES.guided} className={className}>
        {content}
      </Link>
    );
  }
  return (
    <button
      type="button"
      className={className}
      disabled={isLoading}
      onClick={() => firstSession && onPlay(firstSession)}
    >
      {content}
    </button>
  );
}

interface ResumeCardProps {
  session: SessionData;
  position: number;
  duration: number;
  /** False for a premium session the user cannot open any more: the card opens its page (with the lock). */
  canPlay: boolean;
  onResume: () => void;
  title: string;
}

function ResumeCard({ session, position, duration, canPlay, onResume, title }: ResumeCardProps) {
  const headingId = useId();
  const percent = Math.min(100, Math.max(0, (position / duration) * 100));
  const className = cn(
    'flex min-h-11 w-full items-center gap-3 rounded-xl border bg-card p-3 text-left text-card-foreground transition-colors hover:bg-accent',
    FOCUS_RING,
  );
  const content = (
    <>
      {session.image_url ? (
        <span className={cn('relative h-14 w-14 shrink-0 overflow-hidden rounded-lg', SESSION_IMAGE_GRADIENT)}>
          <img src={session.image_url} alt="" className="h-full w-full object-cover" />
        </span>
      ) : (
        <SessionImageFallback className="h-14 w-14 shrink-0 rounded-lg" iconClassName="h-5 w-5" />
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{session.title}</span>
        <span className="block text-xs tabular-nums text-muted-foreground">
          {formatPlaybackTime(position)} / {formatPlaybackTime(duration)}
        </span>
        <span className="mt-1.5 block h-1 overflow-hidden rounded-full bg-muted" aria-hidden="true">
          <span className="block h-full rounded-full bg-primary" style={{ width: `${percent}%` }} />
        </span>
      </span>
      <span
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground"
        aria-hidden="true"
      >
        <Play className="ml-0.5 h-5 w-5" />
      </span>
    </>
  );

  return (
    <section aria-labelledby={headingId}>
      <h2 id={headingId} className="mb-3 text-lg font-semibold sm:text-xl">
        {title}
      </h2>
      {canPlay ? (
        <button type="button" className={className} onClick={onResume}>
          {content}
        </button>
      ) : (
        <Link to={ROUTES.sessionDetail(session.id)} className={className}>
          {content}
        </Link>
      )}
    </section>
  );
}

interface EntryCardProps {
  to: string;
  slot: HomeImageSlot;
  title: string;
  description: string;
  /** The only card (Akustične off): a wide, lower image instead of half a row. */
  wide: boolean;
}

function EntryCard({ to, slot, title, description, wide }: EntryCardProps) {
  return (
    <Link
      to={to}
      className={cn('block h-full overflow-hidden rounded-xl border bg-card text-card-foreground transition-colors hover:bg-accent', FOCUS_RING)}
    >
      <span className={cn('relative block', wide ? 'aspect-[21/9] md:aspect-[4/1]' : 'aspect-[16/10] md:aspect-[16/7]')}>
        <HomeImageFill slot={slot} />
      </span>
      <span className="block space-y-0.5 p-3">
        <span className="block text-sm font-semibold sm:text-base">{title}</span>
        <span className="block text-xs text-muted-foreground sm:text-sm">{description}</span>
      </span>
    </Link>
  );
}

// Unnamed on purpose: a named <section> is a landmark, and the carousel inside is already one.
function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <div className="flex items-end justify-between gap-3">
        <h2 className="text-lg font-semibold sm:text-xl">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export function HomePage() {
  const language = useLanguage();
  const t = (key: HomeTranslationKey) => getHomeTranslation(key, language);
  const isAuthenticated = useIsAuthenticated();
  const user = useUser();
  const playSession = usePlaySession();
  const resumeSession = useResumeSession();
  const resume = useListeningResume();
  const sessionInPlayer = useAudioPlayerStore((state) => (state.isVisible ? state.currentSession?.id : undefined));
  const heroId = useId();

  const { data: sessions, isLoading } = useSessions();
  const { data: favorites } = useFavorites();

  const favoriteIds = useMemo<ReadonlySet<string>>(
    () => new Set(isAuthenticated && favorites ? favorites.map((s: SessionData) => s.id) : []),
    [favorites, isAuthenticated],
  );
  const firstSession = useMemo(
    () => (sessions ? pickFirstSession<SessionData>(sessions, HOME_CURATION.firstSession) : null),
    [sessions],
  );
  const shortBreak = useMemo(
    () => (sessions ? pickShortBreak<SessionData>(sessions, HOME_CURATION.shortBreak) : []),
    [sessions],
  );

  // Q23: only an unfinished session that is still listed and not already open in the player.
  const resumeEntry = useMemo(() => {
    if (!resume || resume.sessionId === sessionInPlayer) return null;
    const session = sessions?.find((s: SessionData) => s.id === resume.sessionId);
    if (!session) return null;
    const duration = resume.duration > 0 ? resume.duration : session.duration;
    return isUnfinished(resume.position, duration) ? { session, position: resume.position, duration } : null;
  }, [resume, sessions, sessionInPlayer]);

  const cards = [
    ...(isAcousticEnabled()
      ? [{ to: ROUTES.acoustic, slot: 'acoustic' as const, title: getBottomNavTranslation('acousticSessions', language), description: t('acousticDesc') }]
      : []),
    { to: ROUTES.guided, slot: 'guided' as const, title: getBottomNavTranslation('guidedSessions', language), description: t('guidedDesc') },
  ];

  return (
    <div className="container space-y-8 py-4 sm:py-8">
      <div className="space-y-1">
        <h1 className="text-2xl font-bold sm:text-3xl">{t('title')}</h1>
        <p className="text-sm text-muted-foreground sm:text-base">{t('subtitle')}</p>
      </div>

      <section aria-labelledby={heroId} className="relative overflow-hidden rounded-2xl">
        <HomeImageFill slot="hero" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/25 to-transparent" aria-hidden="true" />
        <div className="relative flex min-h-[13rem] flex-col justify-end gap-3 p-5 sm:min-h-[16rem] sm:p-8">
          <div>
            <h2 id={heroId} className="text-xl font-semibold text-white sm:text-2xl">
              {t('startHere')}
            </h2>
            <p className="text-sm text-white/90 sm:text-base">{t('startHereDesc')}</p>
          </div>
          <StartButton
            label={t('startFirstSession')}
            isLoading={isLoading}
            firstSession={firstSession}
            onPlay={playSession}
          />
        </div>
      </section>

      {resumeEntry && (
        <ResumeCard
          title={t('continueListening')}
          session={resumeEntry.session}
          position={resumeEntry.position}
          duration={resumeEntry.duration}
          canPlay={!resumeEntry.session.is_premium || hasAccess(user)}
          onResume={() => resumeSession(resumeEntry.session, resumeEntry.position)}
        />
      )}

      <ul className={cn('grid gap-3', cards.length > 1 ? 'grid-cols-2' : 'grid-cols-1')}>
        {cards.map((card) => (
          <li key={card.slot}>
            <EntryCard {...card} wide={cards.length === 1} />
          </li>
        ))}
      </ul>

      {/* The in-app book: only for accounts the server shows it to (ebook-plan D14) */}
      <BookHomeCard />

      <Section title={t('needsTitle')}>
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {HOME_CURATION.needs.map(({ labelKey, icon: Icon, groupKey }) => (
            <li key={labelKey}>
              <Link
                to={ROUTES.guidedCategory(groupKey)}
                className={cn(
                  'flex min-h-11 items-center justify-center gap-2 rounded-full border border-primary/40 px-3 py-2 text-sm font-medium transition-colors hover:bg-accent',
                  FOCUS_RING,
                )}
              >
                <Icon className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                {t(labelKey)}
              </Link>
            </li>
          ))}
        </ul>
      </Section>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
        </div>
      ) : (
        shortBreak.length > 0 && (
          <Section
            title={t('shortBreak')}
            action={
              <Link
                to={ROUTES.guidedDuration('sos')}
                className="-mr-2 inline-flex min-h-11 shrink-0 items-center gap-0.5 rounded-md px-2 text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {getSessionsTranslation('showAll', language)}
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            }
          >
            <HorizontalCarousel label={t('shortBreak')}>
              {shortBreak.map((session) => (
                <SessionTile key={session.id} session={session} isFavorite={favoriteIds.has(session.id)} />
              ))}
            </HorizontalCarousel>
          </Section>
        )
      )}
    </div>
  );
}
